import {FieldArray, Form, Formik} from 'formik'
import {isEmpty} from 'lodash'
import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import Modal from 'rsuite/Modal'
import * as servicePartnerRedux from 'src/app/modules/servicePartner/components/servicePartnerManagement'
import FormInputMediaManager from 'src/cms/helpers/components/forms/FormInputMediaManager'
import FormSelect from 'src/cms/helpers/components/forms/FormSelect'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import * as Yup from 'yup'
import * as abroadRepresentative from '..'
import {AbroadRepresentativeModel, AbroadRepresentativeStaffModel} from '../Model'

type Props = {
  open: boolean
  handleClose: () => void
  onSaved: () => void
  editSelectedData?: any
}

const emptyStaff: AbroadRepresentativeStaffModel = {
  name: '',
  name_np: '',
  email: '',
  number: '',
  designation: '',
  designation_np: '',
}

const FORM_VALIDATION = Yup.object().shape({
  country_id: Yup.string().required('Country is required'),
  representatives: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Name is required'),
        email: Yup.string().email('Invalid email').required('Email is required'),
        number: Yup.string()
          .matches(/^[0-9]+$/, 'Digits only')
          .required('Contact number is required'),
      })
    )
    .min(1, 'Add at least one contact person'),
})

const STAFF_FIELDS: Array<{name: keyof AbroadRepresentativeStaffModel; label: string; required?: boolean}> = [
  {name: 'name', label: 'Name (EN)', required: true},
  {name: 'name_np', label: 'Name (NP)'},
  {name: 'email', label: 'Email', required: true},
  {name: 'number', label: 'Contact Number', required: true},
  {name: 'designation', label: 'Designation (EN)'},
  {name: 'designation_np', label: 'Designation (NP)'},
]

// Add / edit one country's remittance contact persons (legacy SBL contact_person module).
const AddAbroadRepresentative = ({open, handleClose, onSaved, editSelectedData}: Props) => {
  const dispatch = useDispatch()
  const {saving, success} = useSelector((state: any) => state.abroadRepresentative)
  const {countryList} = useSelector((state: any) => state.servicePartner)
  const isEdit = !isEmpty(editSelectedData)

  useEffect(() => {
    dispatch(servicePartnerRedux.actions.servicePartnerCountryList())
  }, [])

  useEffect(() => {
    if (success) {
      toast.success(`Abroad representative ${isEdit ? 'edited' : 'added'} successfully`)
      dispatch(abroadRepresentative.actions.resetAbroadRepresentative())
      onSaved()
    }
  }, [success])

  const countryOptions =
    countryList?.servicePartnerCountryList?.map((c: any) => ({label: c?.title, value: c?.id})) ?? []

  const initialValues: AbroadRepresentativeModel = {
    country_id: editSelectedData?.country?.id ?? '',
    description: editSelectedData?.description ?? '',
    description_np: editSelectedData?.description_np ?? '',
    image: editSelectedData?.image ?? '',
    representatives: isEdit
      ? (editSelectedData?.representatives?.representatives ?? []).map((s: any) => ({
          name: s?.name ?? '',
          name_np: s?.name_np ?? '',
          email: s?.email ?? '',
          number: s?.number ?? '',
          designation: s?.designation ?? '',
          designation_np: s?.designation_np ?? '',
        }))
      : [emptyStaff],
  }

  return (
    <div className='modal-container'>
      <Modal open={open} onClose={handleClose} className='w-75' backdrop='static' keyboard={false} enforceFocus={false}>
        <Modal.Header>
          <Modal.Title>{isEdit ? 'Edit' : 'Add'} Abroad Representative</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={FORM_VALIDATION}
            onSubmit={(values) => {
              isEdit
                ? dispatch(abroadRepresentative.actions.updateAbroadRepresentative(values, editSelectedData?.id))
                : dispatch(abroadRepresentative.actions.addAbroadRepresentative(values))
            }}
          >
            {({touched, handleChange, errors, values, setFieldValue}) => (
              <Form>
                <div className='card-body border-top pt-5'>
                  <div className='row'>
                    <div className='col-md-6 col-xs-12'>
                      <FormSelect
                        labelClassName='col-md-12'
                        containerClassName='col-md-12'
                        placeholder='Please select country'
                        label='Country'
                        name='country_id'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        options={countryOptions}
                        values={values}
                        setFieldValue={setFieldValue}
                        required
                      />
                    </div>
                    <div className='col-md-6 col-xs-12'>
                      <FormInputMediaManager
                        labelClassName='col-md-12'
                        containerClassName='col-md-12'
                        label='Image'
                        name='image'
                        setFieldValue={setFieldValue}
                        value={values?.image}
                      />
                    </div>
                    {['description', 'description_np'].map((name) => (
                      <div className='col-md-6 col-xs-12' key={name}>
                        <FormTextBox
                          as='textarea'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          label={name === 'description' ? 'Description (EN)' : 'Description (NP)'}
                          name={name}
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                    ))}
                  </div>

                  <FieldArray name='representatives'>
                    {({push, remove}) => (
                      <>
                        {values.representatives.map((_, i) => (
                          <div className='row border-top pt-3 mt-3' key={i}>
                            <div className='col-12 d-flex justify-content-between'>
                              <h6>Contact Person {i + 1}</h6>
                              {values.representatives.length > 1 && (
                                <button type='button' className='btn btn-sm btn-light-danger' onClick={() => remove(i)}>
                                  Remove
                                </button>
                              )}
                            </div>
                            {STAFF_FIELDS.map((f) => (
                              <div className='col-md-4 col-xs-12' key={f.name}>
                                <FormTextBox
                                  type='text'
                                  labelClassName='col-md-12'
                                  containerClassName='col-md-12'
                                  label={f.label}
                                  name={`representatives.${i}.${f.name}`}
                                  onChange={handleChange}
                                  errors={errors}
                                  touched={touched}
                                  required={f.required}
                                />
                              </div>
                            ))}
                          </div>
                        ))}
                        <button type='button' className='btn btn-sm btn-light-primary mt-3' onClick={() => push(emptyStaff)}>
                          Add Contact Person
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div>

                <div className='d-flex justify-content-end px-9'>
                  <button type='submit' disabled={saving} className='btn btn-primary btn-sm ms-3'>
                    Save
                  </button>
                  <button type='button' onClick={handleClose} className='btn btn-secondary btn-sm ms-3'>
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AddAbroadRepresentative
